import React, { useEffect, useState } from "react";
import { LogOut, MapPin, Clock, CalendarDays, Loader2, LogIn, CheckCircle2, History, ReceiptText, Printer, Award, Trophy, Flame, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import BrandLogo from "@/components/BrandLogo";
import api, { apiError, fmtTime, fmtDateLong, MONTHS } from "@/lib/api";
import { toast } from "sonner";
import QRScanner from "@/components/QRScanner";
import StatusBadge from "@/components/StatusBadge";
import Payslip from "@/components/Payslip";

const predColor = (p) => ({
  "Disiplin Champion": "bg-violet-100 text-violet-700",
  "Sangat Disiplin": "bg-emerald-100 text-emerald-700",
  "Disiplin": "bg-sky-100 text-[#3368A0]",
  "Cukup Disiplin": "bg-amber-100 text-amber-700",
  "Perlu Improvement": "bg-orange-100 text-orange-700",
  "Perlu Perhatian": "bg-rose-100 text-rose-700",
}[p] || "bg-slate-100 text-slate-600");

export default function KaryawanDashboard() {
  const { user, logout } = useAuth();
  const now = new Date();
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [action, setAction] = useState(null); // 'checkin' | 'checkout'
  const [gpsStatus, setGpsStatus] = useState("");
  const [processing, setProcessing] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [historyMonth, setHistoryMonth] = useState(now.getMonth() + 1);
  const [slip, setSlip] = useState(null);
  const [period, setPeriod] = useState(null);
  const [showSlip, setShowSlip] = useState(false);
  const [disc, setDisc] = useState(null);
  const [board, setBoard] = useState(null);
  const [showDiscDetail, setShowDiscDetail] = useState(false);
  const [schedMonth, setSchedMonth] = useState(now.getMonth() + 1);
  const schedYear = now.getFullYear();
  const [scheds, setScheds] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/attendance/today");
      setCtx(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  const loadExtras = async () => {
    try {
      const [d, b] = await Promise.all([api.get("/discipline/me"), api.get("/discipline/leaderboard")]);
      setDisc(d.data); setBoard(b.data);
    } catch (e) { /* ignore */ }
  };

  const loadScheds = async (m) => {
    try { setScheds((await api.get(`/schedules/me?month=${m}&year=${schedYear}`)).data); } catch (e) { /* ignore */ }
  };

  const loadHistory = async (month) => {
    try {
      const { data } = await api.get(`/attendance/history/me?month=${month}&year=${schedYear}`);
      setHistory(data.rows);
    } catch (e) { toast.error(apiError(e)); }
  };

  const loadSlip = async () => {
    try {
      const [p, s] = await Promise.all([api.get("/payroll/period-info"), api.get("/payroll/me")]);
      setPeriod(p.data); setSlip(s.data);
    } catch (e) { toast.error(apiError(e)); }
  };

  useEffect(() => { load(); loadExtras(); }, []);
  useEffect(() => { loadScheds(schedMonth); }, [schedMonth]); // eslint-disable-line
  useEffect(() => { if (showHistory) loadHistory(historyMonth); }, [historyMonth, showHistory]); // eslint-disable-line

  const startAction = (type) => { setAction(type); setScanning(true); setGpsStatus(""); };

  const getGPS = () =>
    new Promise((resolve, reject) => {
      if (!navigator.geolocation) return reject(new Error("Lokasi diperlukan untuk melakukan absensi."));
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve(pos.coords),
        () => reject(new Error("Lokasi diperlukan untuk melakukan absensi.")),
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      );
    });

  const onDetected = async (qr_token) => {
    setScanning(false);
    setProcessing(true);
    setGpsStatus("Mengambil lokasi GPS...");
    try {
      const coords = await getGPS();
      setGpsStatus(`Akurasi ${Math.round(coords.accuracy)}m, mengirim...`);
      const body = { qr_token, latitude: coords.latitude, longitude: coords.longitude, accuracy: coords.accuracy };
      await api.post(`/attendance/${action}`, body);
      toast.success(action === "checkin" ? "Check-In berhasil!" : "Check-Out berhasil!");
      await load();
      await loadExtras();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setProcessing(false);
      setGpsStatus("");
    }
  };

  if (loading)
    return <div className="min-h-screen flex items-center justify-center bg-[#F2EFE7]"><Loader2 className="h-8 w-8 animate-spin text-[#3368A0]" /></div>;

  const att = ctx?.attendance;
  const status = att?.attendance_status || "BELUM CHECK-IN";
  const todayIso = new Date().toISOString().slice(0, 10);
  const activeScheduleRows = scheds?.rows.filter((schedule) => schedule.schedule_date >= todayIso) || [];

  return (
    <div className="max-w-md mx-auto min-h-screen bg-[#F2EFE7] flex flex-col">
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-slate-200 px-5 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BrandLogo className="h-10 w-10" testId="karyawan-brand-logo" />
          <span className="font-bold text-slate-800">MLC Attendance</span>
        </div>
        <button onClick={logout} data-testid="logout-button" className="text-slate-400 hover:text-rose-600 transition-colors">
          <LogOut className="h-5 w-5" />
        </button>
      </header>

      <main className="flex-1 px-5 py-6 space-y-5">
        <div data-testid="karyawan-greeting-card" className="bg-gradient-to-br from-[#3368A0] to-[#66A3BF] rounded-3xl p-6 text-white shadow-lg">
          <p className="text-sky-100 text-sm">Halo,</p>
          <h1 className="text-2xl font-extrabold">{user.name}</h1>
          <div className="flex items-center gap-1.5 text-sky-100 text-sm mt-3">
            <CalendarDays className="h-4 w-4" />
            {fmtDateLong(todayIso)}
          </div>
        </div>

        {disc && (
          <div className="bg-white rounded-2xl border border-[#C8DFDB] p-5 space-y-4" data-testid="my-discipline-card">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider text-slate-400 font-medium flex items-center gap-1"><Award className="h-3.5 w-3.5" /> Skor Disiplin ({MONTHS[disc.month - 1]})</p>
                <p className="text-3xl font-extrabold text-[#3368A0] mt-1" data-testid="my-discipline-score">{disc.final_score}<span className="text-base text-slate-400 font-medium">/{disc.max_score}</span></p>
                <span className={`inline-block mt-1 text-xs font-semibold px-2 py-0.5 rounded-full ${predColor(disc.predicate)}`} data-testid="my-discipline-predicate">{disc.predicate}</span>
                {disc.has_absence_violation && <p className="text-[11px] text-rose-600 mt-1">⚠ Ada mangkir · predikat maks. "Cukup Disiplin"</p>}
              </div>
              <div className="text-right text-xs text-slate-500 space-y-0.5">
                <p>Skor awal: <b className="text-slate-700">{disc.starting_score}</b></p>
                <p>Bonus: <b className="text-emerald-600">+{disc.total_bonus}</b></p>
                <p>Penalti: <b className="text-rose-600">-{disc.total_penalty}</b></p>
              </div>
            </div>
            <div className="flex items-center justify-between rounded-xl bg-[#F2EFE7] px-3 py-2">
              <span className="text-sm font-semibold text-orange-600 flex items-center gap-1" data-testid="my-discipline-streak"><Flame className="h-4 w-4" /> Streak: {disc.current_streak} hari</span>
              {disc.next_milestone ? (
                <span className="text-xs text-slate-500">Next: {disc.next_milestone.target} hari → +{disc.next_milestone.bonus} poin</span>
              ) : (<span className="text-xs text-emerald-600">Streak maksimal 🎉</span>)}
            </div>
            <button onClick={() => setShowDiscDetail((v) => !v)} data-testid="toggle-discipline-detail"
              className="w-full flex items-center justify-center gap-1 text-xs text-[#3368A0] font-semibold">
              {showDiscDetail ? "Sembunyikan" : "Lihat"} rincian poin
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showDiscDetail ? "rotate-180" : ""}`} />
            </button>
            {showDiscDetail && (
              <div className="space-y-1.5 fade-up" data-testid="discipline-detail">
                {[...(disc.events || []), ...(disc.adjustments || [])].length === 0 ? (
                  <p className="text-center text-slate-400 text-xs py-2">Belum ada aktivitas poin bulan ini.</p>
                ) : (
                  [...(disc.events || []), ...(disc.adjustments || [])].map((e, i) => (
                    <div key={i} className="flex items-center justify-between text-xs border-b border-slate-100 pb-1.5 last:border-0">
                      <div className="min-w-0 pr-2">
                        <p className="text-slate-600 truncate">{e.reason}</p>
                        <p className="text-slate-400">{fmtDateLong(e.event_date)}</p>
                      </div>
                      <span className={`font-bold shrink-0 ${e.points >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{e.points >= 0 ? "+" : ""}{e.points}</span>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}

        {!ctx.has_schedule ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
            <CalendarDays className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600 font-medium" data-testid="no-schedule-message">Anda tidak memiliki jadwal kerja hari ini.</p>
          </div>
        ) : (
          <>
            <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-400 font-medium">Shift Hari Ini</p>
                  <p className="text-lg font-bold text-slate-800">{ctx.shift?.name}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs uppercase tracking-wider text-slate-400 font-medium flex items-center gap-1 justify-end"><Clock className="h-3 w-3" /> Jam Kerja</p>
                  <p className="text-lg font-bold text-slate-800 font-mono">{ctx.shift?.start_time}-{ctx.shift?.end_time}</p>
                </div>
              </div>
              <div className="flex items-center gap-1.5 text-sm text-slate-500">
                <MapPin className="h-4 w-4 text-[#3f7f9c]" /> {ctx.outlet?.name}
              </div>
              <div className="pt-3 border-t border-slate-100 flex items-center justify-between">
                <span className="text-sm text-slate-500">Status Absensi</span>
                <StatusBadge status={status} testid="attendance-status-badge" />
              </div>
            </div>

            {att?.check_in_time && (
              <div className="bg-white rounded-2xl border border-slate-200 p-5 grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-400">Check-In</p>
                  <p className="text-xl font-bold text-[#284f7a] font-mono">{fmtTime(att.check_in_time)}</p>
                  <div className="mt-1"><StatusBadge status={att.punctuality_status} /></div>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Check-Out</p>
                  <p className="text-xl font-bold text-emerald-700 font-mono">{att.check_out_time ? fmtTime(att.check_out_time) : "-"}</p>
                  {att.check_out_status && <div className="mt-1"><StatusBadge status={att.check_out_status} /></div>}
                </div>
              </div>
            )}

            {processing && (
              <div className="bg-sky-50 border border-sky-200 rounded-2xl p-4 flex items-center gap-3" data-testid="gps-status-badge">
                <Loader2 className="h-5 w-5 animate-spin text-[#3368A0]" />
                <span className="text-sm text-[#284f7a]">{gpsStatus}</span>
              </div>
            )}

            {!att?.check_in_time && (
              <Button data-testid="check-in-button" onClick={() => startAction("checkin")} disabled={processing}
                className="w-full h-16 text-lg font-bold bg-[#3368A0] hover:bg-[#284f7a] rounded-2xl pulse-ring">
                <LogIn className="h-6 w-6 mr-2" /> CHECK-IN
              </Button>
            )}
            {att?.check_in_time && !att?.check_out_time && (
              <Button data-testid="check-out-button" onClick={() => startAction("checkout")} disabled={processing}
                className="w-full h-16 text-lg font-bold bg-[#66A3BF] hover:bg-[#5590ab] rounded-2xl pulse-ring">
                <LogOut className="h-6 w-6 mr-2" /> CHECK-OUT
              </Button>
            )}
            {att?.check_out_time && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 flex items-center gap-3 justify-center">
                <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                <span className="font-semibold text-emerald-700">Absensi hari ini selesai</span>
              </div>
            )}
          </>
        )}

        <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3" data-testid="my-schedule-section">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-slate-800 flex items-center gap-1.5"><CalendarDays className="h-4 w-4 text-[#3368A0]" /> Jadwal Kerja</h3>
            <Select value={String(schedMonth)} onValueChange={(v) => setSchedMonth(Number(v))}>
              <SelectTrigger className="w-36 h-9" data-testid="schedule-month-select"><SelectValue /></SelectTrigger>
              <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {!scheds || activeScheduleRows.length === 0 ? (
            <p className="text-center text-slate-400 text-sm py-4" data-testid="no-schedule-month">Tidak ada jadwal pada bulan ini.</p>
          ) : (
            <div className="space-y-2">
              {activeScheduleRows.map((s) => (
                <div key={s.schedule_date} className="flex items-center justify-between border-b border-slate-100 pb-2 last:border-0">
                  <div>
                    <p className="text-sm font-medium text-slate-700">{fmtDateLong(s.schedule_date)}</p>
                    <p className="text-xs text-slate-400">{s.shift_name} · {s.shift_time} · {s.outlet_name}</p>
                  </div>
                  {s.attendance_status && <StatusBadge status={s.attendance_status} />}
                </div>
              ))}
            </div>
          )}
        </div>

        {board && (
          <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3" data-testid="leaderboard-section">
            <h3 className="font-bold text-slate-800 flex items-center gap-1.5"><Trophy className="h-4 w-4 text-amber-500" /> Top 10 Karyawan Terdisiplin ({MONTHS[board.month - 1]})</h3>
            <div className="space-y-1.5">
              {board.rows.slice(0, 10).map((r) => {
                const mine = r.user_id === user.id;
                return (
                  <div key={r.user_id} data-testid={`leaderboard-row-${r.rank}`} className={`flex items-center gap-3 rounded-xl px-3 py-2 ${mine ? "bg-[#C8DFDB]" : "bg-[#F2EFE7]"}`}>
                    <span className={`h-6 w-6 flex items-center justify-center rounded-full text-xs font-bold ${r.rank <= 3 ? "bg-amber-400 text-white" : "bg-white text-slate-500"}`}>{r.rank}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-700 truncate">{r.user_name}{mine && <span className="text-xs text-[#3368A0]"> (Anda)</span>}</p>
                      <p className="text-xs text-slate-400">{r.position}</p>
                    </div>
                    <span className="text-sm font-bold text-[#3368A0]">{r.score}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <button onClick={() => { setShowSlip((s) => !s); if (!showSlip && !slip) loadSlip(); }}
          data-testid="toggle-slip-button"
          className="w-full flex items-center justify-center gap-2 text-[#3368A0] text-sm py-2 font-semibold hover:opacity-80 transition-opacity">
          <ReceiptText className="h-4 w-4" /> {showSlip ? "Sembunyikan" : "Lihat"} Slip Gaji Saya
        </button>

        {showSlip && (
          <div className="space-y-3 fade-up" data-testid="karyawan-slip-section">
            {period && <p className="text-xs text-center text-slate-500">Periode gaji {fmtDateLong(period.period_start)} – {fmtDateLong(period.period_end)} · diperbarui tiap tanggal 26</p>}
            {slip ? (
              <>
                <Payslip slip={slip} />
                <Button data-testid="print-my-payslip-button" onClick={() => window.print()} className="w-full bg-[#66A3BF] hover:bg-[#5590ab] no-print">
                  <Printer className="h-4 w-4 mr-1.5" /> Download / Cetak Slip
                </Button>
              </>
            ) : (
              <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-[#3368A0]" /></div>
            )}
          </div>
        )}

        <button onClick={() => setShowHistory((visible) => !visible)}
          data-testid="toggle-history-button"
          className="w-full flex items-center justify-center gap-2 text-slate-500 text-sm py-2 hover:text-slate-800 transition-colors">
          <History className="h-4 w-4" /> {showHistory ? "Sembunyikan" : "Lihat"} Riwayat Absensi
        </button>

        {showHistory && (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="karyawan-history-table">
            <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
              <p className="text-sm font-semibold text-slate-700">Riwayat Absensi</p>
              <Select value={String(historyMonth)} onValueChange={(value) => setHistoryMonth(Number(value))}>
                <SelectTrigger className="h-9 w-32" data-testid="history-month-select"><SelectValue /></SelectTrigger>
                <SelectContent>{MONTHS.map((monthName, index) => <SelectItem key={monthName} value={String(index + 1)}>{monthName}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {history.length === 0 ? (
              <p className="text-center text-slate-400 text-sm py-6">Belum ada riwayat absensi.</p>
            ) : (
              history.map((h) => (
                <div key={h.id} className="p-4 border-b border-slate-100 last:border-0 flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-slate-700 text-sm">{fmtDateLong(h.schedule_date)}</p>
                    <p className="text-xs text-slate-400">{h.shift_name} · {h.shift_time}</p>
                    <p className="text-xs text-slate-400 font-mono">{fmtTime(h.check_in_time)} → {h.check_out_time ? fmtTime(h.check_out_time) : "-"}</p>
                  </div>
                  <StatusBadge status={h.attendance_status} />
                </div>
              ))
            )}
          </div>
        )}
      </main>

      {scanning && <QRScanner onDetected={onDetected} onCancel={() => setScanning(false)} />}
    </div>
  );
}
